// /home/labdoo/zitech-nexus/frontend/src/services/api.ts
import axios from 'axios';
import { Project, Task } from '../types';

const API_URL = 'https://zitechsolutions.co.tz/api'; // Update with your actual API URL

// Create a public API instance that doesn't require authentication
const publicApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Main API instance with authentication
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle token refresh - FIXED VERSION
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        
        // Only try to refresh if we have a refresh token
        if (!refreshToken) {
          // No refresh token, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(error);
        }
        
        // Try to refresh the token
        const response = await axios.post(`${API_URL}/auth/token/refresh/`, {
          refresh: refreshToken,
        });
        
        // Store the new access token
        const newAccessToken = response.data.access;
        localStorage.setItem('access_token', newAccessToken);
        
        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
        
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// ==================== ANNOUNCEMENT API ====================
export const announcementAPI = {
  // PUBLIC - Get active announcements (no authentication required)
  getActivePublic: async () => {
    try {
      const response = await publicApi.get('/announcements/active/');
      return response;
    } catch (error) {
      console.error('Error fetching public announcements:', error);
      return { data: { active_count: 0, main: null, all: [] } };
    }
  },
  
  // PRIVATE - Get all announcements (authentication required)
  getAll: async (params?: { is_active?: boolean; type?: string; priority?: string; search?: string }) => {
    try {
      const response = await api.get('/announcements/', { params });
      return response;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        console.warn('Authentication required for announcements');
        throw new Error('Please log in to view announcements');
      }
      throw error;
    }
  },
  
  // PRIVATE - Get announcement statistics (authentication required)
  getStats: () => api.get('/announcements/stats/'),
  
  // PRIVATE - Get single announcement (authentication required)
  get: (id: string | number) => api.get(`/announcements/${id}/`),
  
  // PRIVATE - Create announcement with image support
  create: (data: FormData | { [key: string]: any }) => {
    // If data is FormData, use multipart/form-data
    if (data instanceof FormData) {
      return api.post('/announcements/', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    // Otherwise, use JSON
    return api.post('/announcements/', data);
  },
  
  // PRIVATE - Update announcement with image support
  update: (id: string | number, data: FormData | { [key: string]: any }) => {
    // If data is FormData, use multipart/form-data
    if (data instanceof FormData) {
      return api.put(`/announcements/${id}/`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    // Otherwise, use JSON
    return api.put(`/announcements/${id}/`, data);
  },
  
  // PRIVATE - Partial update (authentication required)
  patch: (id: string | number, data: any) => api.patch(`/announcements/${id}/`, data),
  
  // PRIVATE - Delete announcement (authentication required)
  delete: (id: string | number) => api.delete(`/announcements/${id}/`),
  
  // PRIVATE - Toggle status (authentication required)
  toggleStatus: (id: string | number) => api.post(`/announcements/${id}/toggle_status/`),
};

// ==================== AUTH API ====================
export const authAPI = {
  login: (emailOrPhone: string, password: string) =>
    api.post('/auth/login/', { email_or_phone: emailOrPhone, password }),
  
  register: (userData: {
    email: string;
    phone_number: string;
    full_name: string;
    company_name?: string;
    password: string;
    role?: string;
  }) => api.post('/auth/register/', userData),
  
  getProfile: () => api.get('/auth/profile/'),
  
  updateProfile: (data: Partial<{
    full_name: string;
    phone_number: string;
    company_name: string;
  }>) => api.patch('/auth/profile/', data),
  
  logout: (refreshToken: string) => 
    api.post('/auth/logout/', { refresh: refreshToken }),
  
  changePassword: (oldPassword: string, newPassword: string) =>
    api.post('/auth/change-password/', { old_password: oldPassword, new_password: newPassword }),
  
  forgotPassword: (email: string) => 
    api.post('/auth/forgot-password/', { email }),
  
  resetPassword: (uidb64: string, token: string, newPassword: string, confirmPassword: string) => 
    api.post(`/auth/reset-password/${uidb64}/${token}/`, { 
      new_password: newPassword, 
      confirm_password: confirmPassword 
    }),
};

// ==================== SERVICE REQUEST API ====================
export const serviceAPI = {
  getAllServices: () => api.get('/services/'),
  
  createServiceRequest: (formData: FormData) =>
    api.post('/services/create-with-account/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  getMyServiceRequests: () => api.get('/services/my-requests/'),
  
  getServiceRequest: (id: number) => api.get(`/services/my-requests/${id}/`),
  
  updateServiceRequestStatus: (id: number, status: string) =>
    api.patch(`/services/my-requests/${id}/`, { status }),
  
  cancelServiceRequest: (id: number) => 
    api.post(`/services/my-requests/${id}/cancel/`),
};

// ==================== TRAINING API ====================
export const trainingAPI = {
  getMyEnrollments: () => api.get('/services/trainings/my-enrollments/'),
  
  getEnrollment: (id: number) => api.get(`/services/trainings/enrollments/${id}/`),
  
  startTraining: (requestId: number, technologies: string[]) => 
    api.post(`/services/trainings/start/${requestId}/`, { technologies }),
  
  markContentComplete: (contentId: number, enrollmentId: number) => 
    api.post(`/services/trainings/content/${contentId}/complete/`, { enrollment_id: enrollmentId }),
  
  getModules: (technology: string) => api.get(`/services/trainings/modules/${technology}/`),
  
  getContent: (contentId: number) => api.get(`/services/trainings/content/${contentId}/`),
};

// ==================== ADMIN TRAINING API ====================
export const adminTrainingAPI = {
  getAllModules: () => api.get('/services/admin/training/modules/'),
  getModule: (id: number) => api.get(`/services/admin/training/modules/${id}/`),
  createModule: (data: {
    technology_name: string;
    title: string;
    description: string;
    difficulty: string;
    duration_hours: number;
    order: number;
    is_active: boolean;
  }) => api.post('/services/admin/training/modules/', data),
  updateModule: (id: number, data: any) => api.patch(`/services/admin/training/modules/${id}/`, data),
  deleteModule: (id: number) => api.delete(`/services/admin/training/modules/${id}/`),
  
  getModuleContents: (moduleId: number) => api.get(`/services/admin/training/modules/${moduleId}/contents/`),
  getContent: (id: number) => api.get(`/services/admin/training/contents/${id}/`),
  
  createContent: (moduleId: number, formData: FormData) => 
    api.post(`/services/admin/training/modules/${moduleId}/contents/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  updateContent: (id: number, formData: FormData) => 
    api.put(`/services/admin/training/contents/${id}/`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  deleteContent: (id: number) => api.delete(`/services/admin/training/contents/${id}/`),
  
  deleteAttachment: (attachmentId: number) => 
    api.delete(`/services/admin/training/attachments/${attachmentId}/`),
  
  getAllEnrollments: () => api.get('/services/admin/training/enrollments/'),
  getEnrollment: (id: number) => api.get(`/services/admin/training/enrollments/${id}/`),
  updateEnrollment: (id: number, data: { status: string; progress_percentage?: number }) => 
    api.patch(`/services/admin/training/enrollments/${id}/`, data),
};

// ==================== USER TRAINING API ====================
export const userTrainingAPI = {
  getMyEnrollments: () => api.get('/services/trainings/my-enrollments/'),
  
  getEnrollment: (id: number) => api.get(`/services/trainings/enrollments/${id}/`),
  
  startTraining: (requestId: number) => api.post(`/services/trainings/start/${requestId}/`),
  
  startTrainingSession: (enrollmentId: number) => 
    api.post(`/services/trainings/enrollments/${enrollmentId}/start/`),
  
  getModules: (technology: string) => api.get(`/services/trainings/modules/${technology}/`),
  
  getContent: (id: number) => api.get(`/services/trainings/content/${id}/`),
  
  startContentProgress: (contentId: number, enrollmentId: number) => 
    api.post(`/services/trainings/content/${contentId}/start/`, { enrollment_id: enrollmentId }),
  
  completeContentProgress: (contentId: number, enrollmentId: number, timeSpentSeconds: number, score?: number) => 
    api.post(`/services/trainings/content/${contentId}/complete/`, {
      enrollment_id: enrollmentId,
      time_spent_seconds: timeSpentSeconds,
      score: score
    }),
};

// ==================== ADMIN API ====================
export const adminAPI = {
  getDashboardStats: () => api.get('/services/dashboard-stats/'),
  
  getAllUsers: () => api.get('/services/admin/users/'),
  getUser: (id: number) => api.get(`/services/admin/users/${id}/`),
  createUser: (userData: {
    email: string;
    phone_number: string;
    full_name: string;
    company_name?: string;
    password: string;
    role: string;
  }) => api.post('/services/admin/users/', userData),
  updateUser: (id: number, userData: Partial<{
    full_name: string;
    phone_number: string;
    company_name: string;
    role: string;
    is_active: boolean;
  }>) => api.patch(`/services/admin/users/${id}/`, userData),
  deleteUser: (id: number) => api.delete(`/services/admin/users/${id}/`),
  
  getAllServices: () => api.get('/services/admin/services/'),
  getService: (id: number) => api.get(`/services/admin/services/${id}/`),
  createService: (serviceData: {
    name: string;
    description: string;
    is_active: boolean;
  }) => api.post('/services/admin/services/', serviceData),
  updateService: (id: number, serviceData: Partial<{
    name: string;
    description: string;
    is_active: boolean;
  }>) => api.patch(`/services/admin/services/${id}/`, serviceData),
  deleteService: (id: number) => api.delete(`/services/admin/services/${id}/`),
  
  getAllRequests: () => api.get('/services/admin/requests/'),
  getRequest: (id: number) => api.get(`/services/admin/requests/${id}/`),
  updateRequestStatus: (id: number, status: string) =>
    api.patch(`/services/admin/requests/${id}/`, { status }),
  deleteRequest: (id: number) => api.delete(`/services/admin/requests/${id}/`),

  getUserEnrollments: (userId: number) => 
    api.get(`/services/admin/training/enrollments/?user=${userId}`),
};

// ==================== PROJECT API ====================
export const projectAPI = {
  getAll: () => api.get('/projects/projects/'),
  getById: (id: number) => api.get(`/projects/projects/${id}/`),
  create: (data: Partial<Project>) => api.post('/projects/projects/', data),
  update: (id: number, data: Partial<Project>) =>
    api.patch(`/projects/projects/${id}/`, data),
  delete: (id: number) => api.delete(`/projects/projects/${id}/`),
  getTasks: (projectId: number) => api.get(`/projects/tasks/?project=${projectId}`),
};

// ==================== TASK API ====================
export const taskAPI = {
  getAll: () => api.get('/projects/tasks/'),
  create: (data: Partial<Task>) => api.post('/projects/tasks/', data),
  update: (id: number, data: Partial<Task>) =>
    api.patch(`/projects/tasks/${id}/`, data),
  delete: (id: number) => api.delete(`/projects/tasks/${id}/`),
};

// ==================== USER ACTIVITY API ====================
export const activityAPI = {
  getUserActivities: () => api.get('/accounts/activities/'),
  getRecentActivities: (limit: number = 10) => 
    api.get(`/accounts/activities/recent/?limit=${limit}`),
};

// ==================== DASHBOARD API ====================
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats/'),
  getRecentRequests: () => api.get('/dashboard/recent-requests/'),
};

// ==================== CONTACT API ====================
export const contactAPI = {
  submitContact: (data: { name: string; email: string; message: string }) =>
    api.post('/contact/submit/', data),
  
  getAllMessages: () => api.get('/contact/admin/messages/'),
  getMessage: (id: number) => api.get(`/contact/admin/messages/${id}/`),
  markAsRead: (id: number) => api.patch(`/contact/admin/messages/${id}/`, { is_read: true }),
  sendReply: (id: number, data: { reply_message: string }) =>
    api.post(`/contact/admin/messages/${id}/reply/`, data),
};

// ==================== JOBS API ====================
export const jobsAPI = {
  getActiveJobs: () => api.get('/jobs/jobs/'),
  getJobDetail: (id: number) => api.get(`/jobs/jobs/${id}/`),
  submitApplication: (formData: FormData) => 
    api.post('/jobs/apply/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  
  getAllJobs: () => api.get('/jobs/admin/jobs/'),
  createJob: (data: any) => api.post('/jobs/admin/jobs/', data),
  updateJob: (id: number, data: any) => api.patch(`/jobs/admin/jobs/${id}/`, data),
  deleteJob: (id: number) => api.delete(`/jobs/admin/jobs/${id}/`),
  getAllApplications: () => api.get('/jobs/admin/applications/'),
  getApplication: (id: number) => api.get(`/jobs/admin/applications/${id}/`),
  updateApplicationStatus: (id: number, status: string) => 
    api.patch(`/jobs/admin/applications/${id}/`, { status }),

  getUserApplications: () => api.get('/jobs/my-applications/'),
  getJobApplications: (jobId: number) => api.get(`/jobs/admin/applications/?job=${jobId}`),
};

// ==================== BLOG API ====================
export const blogAPI = {
  getCategories: () => api.get('/blog/categories/'),
  getPosts: (params?: { search?: string; category?: string }) => 
    api.get('/blog/posts/', { params }),
  getPostBySlug: (slug: string) => api.get(`/blog/posts/${slug}/`),
  submitComment: (data: { post: number; author_name: string; author_email: string; content: string }) =>
    api.post('/blog/comments/', data),
  
  adminGetCategories: () => api.get('/blog/admin/categories/'),
  adminCreateCategory: (data: any) => api.post('/blog/admin/categories/', data),
  adminUpdateCategory: (id: number, data: any) => api.patch(`/blog/admin/categories/${id}/`, data),
  adminDeleteCategory: (id: number) => api.delete(`/blog/admin/categories/${id}/`),
  
  // Fixed: Get all posts (no id parameter)
  adminGetPosts: () => api.get('/blog/admin/posts/'),
  
  // Get single post by id (for editing)
  adminGetPost: (id: number) => api.get(`/blog/admin/posts/${id}/`),
  
  adminCreatePost: (formData: FormData) => api.post('/blog/admin/posts/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  adminUpdatePost: (id: number, formData: FormData) => api.patch(`/blog/admin/posts/${id}/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  adminDeletePost: (id: number) => api.delete(`/blog/admin/posts/${id}/`),
  
  adminGetComments: () => api.get('/blog/admin/comments/'),
  adminApproveComment: (id: number) => api.patch(`/blog/admin/comments/${id}/`, { is_approved: true }),
  adminDeleteComment: (id: number) => api.delete(`/blog/admin/comments/${id}/`),
};

// ==================== SUPPORT API ====================
export const supportAPI = {
  createTicket: (data: { name: string; email: string; subject: string; message: string }) =>
    api.post('/support/create/', data),
  
  getMyTickets: () => api.get('/support/my-tickets/'),
  getTicketDetail: (id: number) => api.get(`/support/tickets/${id}/`),
  addReply: (ticketId: number, message: string) =>
    api.post(`/support/tickets/${ticketId}/reply/`, { message }),
  
  adminGetAllTickets: () => api.get('/support/admin/tickets/'),
  adminGetTicketDetail: (id: number) => api.get(`/support/admin/tickets/${id}/`),
  adminUpdateTicketStatus: (id: number, status: string) =>
    api.patch(`/support/admin/tickets/${id}/`, { status }),
  adminGetStats: () => api.get('/support/admin/stats/'),
};

// ==================== NEWSLETTER API ====================
export const newsletterAPI = {
  subscribe: (data: { email: string; name?: string }) => {
    return api.post('/newsletter/subscribe/', data);
  },
  
  unsubscribe: (data: { email: string; reason?: string }) => {
    return api.post('/newsletter/unsubscribe/', data);
  },
  
  confirmSubscription: (token: string) => {
    return api.get(`/newsletter/confirm/${token}/`);
  },
  
  getSubscribers: (params?: { status?: string; search?: string }) => {
    return api.get('/newsletter/subscribers/', { params });
  },
  
  getSubscriberStats: () => {
    return api.get('/newsletter/stats/');
  },
  
  exportSubscribers: () => {
    return api.get('/newsletter/subscribers/export/', { responseType: 'blob' });
  },
  
  deleteSubscriber: (data: { email: string }) => {
    return api.delete('/newsletter/subscribers/delete/', { data });
  },
  
  resendConfirmation: (data: { email: string }) => {
    return api.post('/newsletter/resend-confirmation/', data);
  },
  
  bulkUnsubscribe: (data: { emails: string[]; reason?: string }) => {
    return api.post('/newsletter/subscribers/bulk-unsubscribe/', data);
  },
  
  bulkDelete: (data: { emails: string[] }) => {
    return api.delete('/newsletter/subscribers/bulk-delete/', { data });
  },
  
  bulkResendConfirmation: (data: { emails: string[] }) => {
    return api.post('/newsletter/subscribers/bulk-resend/', data);
  },
  
  getAnalytics: () => {
    return api.get('/newsletter/analytics/');
  },
  
  getCampaigns: () => {
    return api.get('/newsletter/campaigns/');
  },
  
  createCampaign: (data: any) => {
    return api.post('/newsletter/campaigns/', data);
  },
  
  sendCampaign: (campaignId: string) => {
    return api.post(`/newsletter/campaigns/${campaignId}/send/`);
  },
  
  getSettings: () => {
    return api.get('/newsletter/settings/');
  },
  
  updateSettings: (settings: any) => {
    return api.put('/newsletter/settings/', settings);
  },
};

// ==================== PORTFOLIO API ====================
export const portfolioAPI = {
  getProjects: (params?: { category?: string; featured?: boolean; search?: string }) => {
    return api.get('/portfolio/projects/', { params });
  },
  
  getProject: (id: string) => {
    return api.get(`/portfolio/projects/${id}/`);
  },
  
  getSolutions: (params?: { featured?: boolean; search?: string }) => {
    return api.get('/portfolio/solutions/', { params });
  },
  
  getSolution: (id: string) => {
    return api.get(`/portfolio/solutions/${id}/`);
  },
  
  getSettings: () => {
    return api.get('/portfolio/settings/');
  },
  
  getCategories: () => {
    return api.get('/portfolio/categories/');
  },
  
  adminGetProjects: () => {
    return api.get('/portfolio/admin/projects/');
  },
  
  adminGetProject: (id: string) => {
    return api.get(`/portfolio/admin/projects/${id}/`);
  },
  
  adminCreateProject: (data: FormData) => {
    return api.post('/portfolio/admin/projects/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  adminUpdateProject: (id: string, data: any) => {
    return api.put(`/portfolio/admin/projects/${id}/`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  
  adminDeleteProject: (id: string) => {
    return api.delete(`/portfolio/admin/projects/${id}/`);
  },
  
  adminGetSolutions: () => {
    return api.get('/portfolio/admin/solutions/');
  },
  
  adminCreateSolution: (data: FormData) => {
    return api.post('/portfolio/admin/solutions/', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  adminUpdateSolution: (id: string, data: any) => {
    return api.put(`/portfolio/admin/solutions/${id}/`, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },
  
  adminDeleteSolution: (id: string) => {
    return api.delete(`/portfolio/admin/solutions/${id}/`);
  },
  
  adminGetSolution: (id: string) => {
    return api.get(`/portfolio/admin/solutions/${id}/`);
  },
};

// ==================== CLASS MARKS API ====================
export const classMarksAPI = {
  getRecords: () => api.get('/classmarks/records/'),
  getRecord: (id: number) => api.get(`/classmarks/records/${id}/`),
  createRecord: (data: {
    school_name: string;
    school_registration_number?: string;
    class_name: string;
    term: string;
    exam_type: string;
    exam_year: number;
    exam_month?: string;
    center_number?: string;
    center_name?: string;
  }) => api.post('/classmarks/records/create/', data),
  updateRecord: (id: number, data: Partial<{
    school_name: string;
    school_registration_number: string;
    class_name: string;
    term: string;
    exam_type: string;
    exam_year: number;
    exam_month: string;
    center_number: string;
    center_name: string;
    status: string;
  }>) => api.patch(`/classmarks/records/${id}/`, data),
  deleteRecord: (id: number) => api.delete(`/classmarks/records/${id}/`),
  importExcel: (recordId: number, file: File) => {
    const formData = new FormData();
    formData.append('excel_file', file);
    return api.post(`/classmarks/records/${recordId}/import/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  updateStudentMarks: (recordId: number, studentId: number, data: Partial<{
    history_score: number;
    geography_score: number;
    kiswahili_score: number;
    english_score: number;
    physics_score: number;
    chemistry_score: number;
    biology_score: number;
    basic_math_score: number;
    bookkeeping_score: number;
    commerce_score: number;
    bible_knowledge_score: number;
    civics_score: number;
    agriculture_score: number;
    arabic_score: number;
    sports_score: number;
    french_score: number;
    is_absent: boolean;
  }>) => api.put(`/classmarks/records/${recordId}/students/${studentId}/`, data),
  getStats: (recordId: number) => api.get(`/classmarks/records/${recordId}/stats/`),
};

// ==================== HELPER FUNCTIONS ====================

export const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('access_token');
  return !!token;
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }
  return null;
};

export const isSuperAdmin = (): boolean => {
  const user = getCurrentUser();
  return user?.role === 'superadmin';
};

export const isAdmin = (): boolean => {
  const user = getCurrentUser();
  return user?.role === 'superadmin' || user?.role === 'admin';
};

export const hasPermission = (userRole: string, requiredRole: string): boolean => {
  const roleLevel: Record<string, number> = {
    'user': 1,
    'trainee': 1,
    'admin': 2,
    'superadmin': 3,
  };
  return roleLevel[userRole] >= roleLevel[requiredRole];
};

export const getRoleDisplayName = (role: string): string => {
  const names: Record<string, string> = {
    'superadmin': 'Super Administrator',
    'admin': 'Administrator',
    'user': 'User',
    'trainee': 'Trainee',
  };
  return names[role] || role;
};

export const getRoleBadgeColor = (role: string): string => {
  const colors: Record<string, string> = {
    'superadmin': '#ffd89b',
    'admin': '#667eea',
    'user': '#28a745',
    'trainee': '#17a2b8',
  };
  return colors[role] || '#6c757d';
};

export const getStatusDisplayName = (status: string): string => {
  const names: Record<string, string> = {
    'pending': 'Pending',
    'in_review': 'In Review',
    'approved': 'Approved',
    'in_progress': 'In Progress',
    'completed': 'Completed',
    'cancelled': 'Cancelled',
  };
  return names[status] || status;
};

export const getStatusBadgeColor = (status: string): string => {
  const colors: Record<string, string> = {
    'pending': '#ffc107',
    'in_review': '#17a2b8',
    'approved': '#28a745',
    'in_progress': '#007bff',
    'completed': '#28a745',
    'cancelled': '#dc3545',
  };
  return colors[status] || '#6c757d';
};

export const logout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export default api;